<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import InvestigationGroupDrawer from '@/views/quality/master/InvestigationGroupDrawer.vue'
import InvestigationGroupService from '@/services/quality/investigationGroup.service'

const searchQuery = ref('')
const debouncedSearch = ref('')
const isConfirmDialogVisible = ref(false)

// Debounce search 500ms so typing doesn't fire a request per keystroke
let searchTimeout = null

watch(searchQuery, val => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    debouncedSearch.value = val
    page.value = 1
  }, 500)
})

definePage({
  meta: {
    name: 'incident-master-investigation-group',
    subject: 'quality.menu',
    action: 'master_investigation_group',
  },  
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    // Relation, not a column — the API can't order by it.
    title: 'Users',
    key: 'users',
    sortable: false,
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: investigationGroupListing,
  execute: fetchInvestigationGroupListing,
} =  await useApi(createUrl('/masters/investigation-groups', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const investigationGroups = computed(() => investigationGroupListing.value?.data)
const totalInvestigationGroups = computed(() => investigationGroupListing.value?.meta?.total)

const isDrawerVisible = ref(false)
const isViewOnly = ref(false)
const investigationGroupData = ref()

const showInvestigationGroup = (data = null, viewOnly = false) => {
  investigationGroupData.value = data
  isDrawerVisible.value = true
  isViewOnly.value = viewOnly
}

watch(isDrawerVisible, value => {
  if (value === false) {
    investigationGroupData.value = null
    isViewOnly.value = false
  }
})

const refConfirmDialog = ref()

const showConfirmDialog = data => {
  investigationGroupData.value = data
  isConfirmDialogVisible.value = true
}

const deleteInvestigationGroup = async (confirm, data) => {
  if(data?.id && confirm){
    InvestigationGroupService.destroy(
      data.id, 
      () => {
        refConfirmDialog.value.onSuccess()
        isConfirmDialogVisible.value = false
        fetchInvestigationGroupListing()
      },
      () => {
        refConfirmDialog.value.onError()
      },
    )
  }
}

watch(isConfirmDialogVisible, value => {
  if (value === false) investigationGroupData.value = null
})
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap align-center py-4 gap-4">
        <!-- 👉 Search -->
        <div class="group-search-field">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>

        <VSpacer />

        <VBtn
          v-if="$can('create', 'master.form_type')"
          prepend-icon="tabler-plus"
          @click="showInvestigationGroup(null)"
        >
          Add Investigation Group
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="investigationGroups"
        :items-length="totalInvestigationGroups"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <button
            type="button"
            class="group-name text-body-1 font-weight-medium text-start"
            @click="showInvestigationGroup(item, true)"
          >
            {{ item.name }}
          </button>
        </template>

        <template #item.users="{ item }">
          <!--
            Users carry no colour of their own, so they fall back to defaultColor.
            Tonal keeps the stack calm and lets the flat primary +N read as the
            "more" affordance rather than just another member.
          -->
          <AppAvatarGroup
            v-if="item.users?.length"
            :items="item.users"
            :max="4"
            :size="36"
            default-color="primary"
            variant="tonal"
          />
          <span
            v-else
            class="text-medium-emphasis"
          >—</span>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn 
            v-if="$can('delete', 'master.form_type')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn 
            v-if="$can('update', 'master.form_type')"
            @click="showInvestigationGroup(item)"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>
    <InvestigationGroupDrawer
      v-model:isDrawerOpen="isDrawerVisible"
      :form-data="investigationGroupData"
      :view-only="isViewOnly"
      @refetch-listing="fetchInvestigationGroupListing"
    />
    <ConfirmDialog
      ref="refConfirmDialog"
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Investigation Group Deleted."
      confirm-title="Deleted!"
      cancel-title="Error!"
      cancel-msg="Something Went Wrong."
      :is-confirm-visible="false"
      :is-error-visible="false"
      :data="investigationGroupData"
      @confirm="deleteInvestigationGroup"
    />
  </section>
</template>

<style>
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>

<style lang="scss" scoped>
.group-search-field {
  inline-size: 100%;

  @media (min-width: 600px) {
    inline-size: 20rem;
  }
}

// Rendered as a real button so the row opens from the keyboard too,
// but styled as plain text to sit naturally in the cell.
.group-name {
  padding: 0;
  border: 0;
  background: none;
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
  font: inherit;
  transition: color 0.15s ease;

  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
